const VERSION_SKIP = new Set(['', '-1', 'all', 'ALL', '__OTHER__']);

function dayStartMs(yyyymmdd) {
  const text = String(yyyymmdd || '');
  if (!/^\d{8}$/.test(text)) {
    return null;
  }
  const year = Number(text.slice(0, 4));
  const month = Number(text.slice(4, 6)) - 1;
  const day = Number(text.slice(6, 8));
  return new Date(year, month, day, 0, 0, 0, 0).getTime();
}

function nextDayStartMs(yyyymmdd) {
  const start = dayStartMs(yyyymmdd);
  if (start == null) {
    return null;
  }
  const next = new Date(start);
  next.setDate(next.getDate() + 1);
  return next.getTime();
}

function dayEndInclusiveMs(yyyymmdd) {
  const next = nextDayStartMs(yyyymmdd);
  if (next == null) {
    return null;
  }
  return next - 1000;
}

function activeTextList(list) {
  return (list || [])
    .map(item => String(item).trim())
    .filter(item => item && item !== 'all');
}

function readExtraIssueFilters(enabled, filterData) {
  if (!enabled) {
    return { tags: [], tagNot: false, statuses: [] };
  }
  const rows = filterData || [];
  const tagRow = rows.find(row => row && row.name === 'tagList');
  const statusRow = rows.find(row => row && row.name === 'status');
  const tagValue = tagRow && tagRow.value && typeof tagRow.value === 'object' && !Array.isArray(tagRow.value)
    ? tagRow.value
    : {};
  const tags = activeTextList(tagValue.value);
  const statuses = activeTextList(statusRow && statusRow.value);
  return {
    tags,
    tagNot: !!tagValue.isMustNot && tags.length > 0,
    statuses,
  };
}

function activeVersions(versionList) {
  return (versionList || [])
    .map(item => String(item).trim())
    .filter(item => item && !VERSION_SKIP.has(item));
}

function activeVmTypes(vmTypeList, supported) {
  if (!supported) {
    return [];
  }
  return (vmTypeList || [])
    .map(item => Number(item))
    .filter(item => Number.isFinite(item) && item !== 0);
}

function activeScenes(sceneList, enabled) {
  if (!enabled) {
    return [];
  }
  return (sceneList || [])
    .map(item => String(item).trim())
    .filter(Boolean);
}

function activeCountries(countryList, enabled, countryGroupConfigList) {
  if (!enabled) {
    return [];
  }
  const configs = countryGroupConfigList || [];
  const out = [];
  (countryList || []).forEach(raw => {
    const text = String(raw || '').trim();
    if (!text || text === '-1') {
      return;
    }
    if (text.startsWith('GRP_')) {
      const id = Number(text.slice(4));
      const config = configs.find(item => Number(item.id) === id);
      if (config && Array.isArray(config.countries)) {
        config.countries.forEach(code => {
          if (code) {
            out.push(String(code));
          }
        });
      }
      return;
    }
    out.push(text);
  });
  return Array.from(new Set(out));
}

function isDateNarrowed(minDate, maxDate, defaultDate) {
  if (!minDate) {
    return false;
  }
  const end = maxDate || minDate;
  if (end !== minDate) {
    return true;
  }
  return minDate !== defaultDate;
}

/**
 * 每日 TOP 问题链接。
 * 运行环境 / 版本 / 日期 / 国家 / 场景 / 标签 / 处理状态都停在默认时，保持问题详情。
 * 任一维度收窄后，打开高级搜索，条件之间为 AND，未收窄的维度不写入。
 */
export function buildTopIssueWebsiteUrl(input) {
  const {
    origin,
    issueDetailPathPrefix,
    appId,
    issueId,
    platformId,
    crashDataType,
    vmTypeSupported,
    vmTypeList,
    versionList,
    countryEnabled,
    countryList,
    countryGroupConfigList,
    sceneEnabled,
    sceneList,
    minDate,
    maxDate,
    defaultDate,
    issueFilterEnabled,
    issueFilterData,
  } = input || {};

  const vmTypes = activeVmTypes(vmTypeList, vmTypeSupported);
  const versions = activeVersions(versionList);
  const countries = activeCountries(countryList, countryEnabled, countryGroupConfigList);
  const scenes = activeScenes(sceneList, sceneEnabled);
  const extra = readExtraIssueFilters(issueFilterEnabled, issueFilterData);
  const dateNarrowed = isDateNarrowed(minDate, maxDate, defaultDate);
  const narrowed = vmTypes.length > 0 || versions.length > 0 || countries.length > 0 || scenes.length > 0
    || dateNarrowed || extra.tags.length > 0 || extra.statuses.length > 0;

  const legacyUrl = `${origin}/${issueDetailPathPrefix}/${appId}/${issueId}?pid=${platformId}&crashDataType=${crashDataType}`;
  if (!narrowed) {
    return { url: legacyUrl, jumpKind: 'plain' };
  }

  const conditions = [{
    field: 'issueId',
    queryType: 'TERMS',
    terms: [String(issueId)],
  }];

  const rangeStart = dayStartMs(minDate);
  const rangeEnd = dayEndInclusiveMs(maxDate || minDate);
  if (rangeStart != null && rangeEnd != null) {
    conditions.push({
      field: 'crashUploadTime',
      queryType: 'RANGE',
      gte: rangeStart,
      lte: rangeEnd,
    });
  }
  if (vmTypes.length > 0) {
    conditions.push({
      field: 'vmType',
      queryType: 'TERMS',
      terms: vmTypes,
    });
  }
  if (versions.length > 0) {
    conditions.push({
      field: 'version',
      queryType: 'TERMS_WILDCARD',
      terms: versions,
      not: false,
    });
  }
  if (countries.length > 0) {
    conditions.push({
      field: 'countryOrRegion',
      queryType: 'TERMS',
      terms: countries,
    });
  }
  if (scenes.length > 0) {
    conditions.push({
      field: 'userSceneTag',
      queryType: 'TERMS',
      terms: scenes,
    });
  }
  if (extra.tags.length > 0) {
    conditions.push({
      field: 'issueTag',
      queryType: 'TERMS',
      terms: extra.tags,
      not: extra.tagNot,
    });
  }
  if (extra.statuses.length > 0) {
    conditions.push({
      field: 'issueStatus',
      queryType: 'TERMS',
      terms: extra.statuses,
    });
  }

  const query = new URLSearchParams({
    pid: String(platformId),
    searchConditionGroup: JSON.stringify({ conditions }),
  }).toString();
  return {
    url: `${origin}/crash-reporting/advanced-search/${appId}?${query}`,
    jumpKind: 'filtered',
  };
}
